import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { booleanSchema } from '@zaoblako/shared/modules/serde/schemas/boolean.schema';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { PaymentScenarionConditionKindEnum } from '@zaoblako/shared/modules/task/enum/payment.scenarion.condition.kind.enum';
import { PaymentScenarionConditionTypeEnum } from '@zaoblako/shared/modules/task/enum/payment.scenarion.condition.type.enum';
import { PaymentScenarionConditionKindEnumI18n } from '@zaoblako/shared/modules/task/i18n/payment-scenarion-condition-kind.enum.i18n';
import { PaymentScenarionConditionTypeEnumI18n } from '@zaoblako/shared/modules/task/i18n/payment-scenarion-condition-type.enum.i18n';
import { TaskAnalyticsModeEnum } from '@zaoblako/shared/modules/task/enum/task-analytics-mode.enum';
import { TaskAnalyticsModeEnumI18n } from '@zaoblako/shared/modules/task/i18n/task-analytics-mode-enum.i18n';
import { TaskAnalyticsKindEnum } from '@zaoblako/shared/modules/task/enum/task-analytics-kind.enum';
import { TaskAnalyticsKindEnumI18n } from '@zaoblako/shared/modules/task/i18n/task-analytics-kind-enum.i18n';
import type { PaymentScenarionFindInterface } from '@zaoblako/shared/modules/task/dtos/payment-scenarion/payment-scenarion-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';

import * as yup from 'yup';

/** Payment scenarion condition request body */
export interface PaymentScenarionConditionFormInterface {
  /** Unique identifier */
  id?: number;
  /** Condition kind - which fields to compare */
  kind: PaymentScenarionConditionKindEnum;
  /** Condition type - how fields should be compared */
  type: PaymentScenarionConditionTypeEnum;
  /** Amount condition */
  amount?: number;
  /** Exceeding plan condition */
  exceedingPlan?: boolean;
  /** Exhausted budget percent condition */
  exhaustedBudgetPercent?: number;
  /**
   * If {@link PaymentScenarionConditionFormInterface.kind} equals {@link PaymentScenarionConditionKindEnum.EXCEEDING_PLAN},
   * then payment task will take into account exceeding the plan by analytics in this mode
   */
  excceedingPlanAnalyticsMode?: TaskAnalyticsModeEnum;
  /**
   * If {@link PaymentScenarionConditionFormInterface.kind} equals {@link PaymentScenarionConditionKindEnum.EXCEEDING_PLAN},
   * then payment task will take into account exceeding the plan by analytics of this kind
   */
  excceedingPlanAnalyticsKind?: TaskAnalyticsKindEnum;
  /** Companies condition */
  companies?: CompanyFindInterface[];
  /** Directions condition */
  directions?: DirectionFindInterface[];
  /** Units condition */
  units?: UnitFindInterface[];
  /** Filials condition */
  filials?: FilialFindInterface[];
  /** Deals condition */
  deals?: DealFindInterface[];
  /** Cost centers condition */
  costCenters?: CostCenterFindInterface[];
  /** Psyment scenarion */
  paymentScenarion: PaymentScenarionFindInterface;
}

/** Payment scenarion DTO */
export const PaymentScenarionConditionFormDto = SerdeDtoClass.create<PaymentScenarionConditionFormInterface>(
  TaskDtoKeysEnum.PAYMENT_SCENARION_CONDITION_FORM_DTO,
  {
    id: {
      optional: true,
      label: 'Номер',
      serializer: yup.number(),
    },
    kind: {
      label: 'Вид',
      serializer: commonEnumValidator(PaymentScenarionConditionKindEnum, PaymentScenarionConditionKindEnumI18n),
    },
    type: {
      label: 'Тип',
      serializer: commonEnumValidator(PaymentScenarionConditionTypeEnum, PaymentScenarionConditionTypeEnumI18n),
    },
    amount: {
      optional: true,
      label: 'Сумма',
      serializer: yup.number(),
    },
    exceedingPlan: {
      optional: true,
      label: 'Превышение плана',
      serializer: booleanSchema,
    },
    exhaustedBudgetPercent: {
      optional: true,
      label: 'Бюджет исчерпан (в процентах)',
      serializer: yup.number(),
    },
    excceedingPlanAnalyticsMode: {
      optional: true,
      label: 'Какую аналитику использовать для превышения плана',
      serializer: commonEnumValidator(TaskAnalyticsModeEnum, TaskAnalyticsModeEnumI18n),
    },
    excceedingPlanAnalyticsKind: {
      optional: true,
      label: 'Аналитику на основании БС или ее группы использовать для превышения плана',
      serializer: commonEnumValidator(TaskAnalyticsKindEnum, TaskAnalyticsKindEnumI18n),
    },
    companies: {
      optional: true,
      label: 'Компании',
      serializer: yup.array(entityLikeSchema),
    },
    directions: {
      optional: true,
      label: 'БС',
      serializer: yup.array(entityLikeSchema),
    },
    units: {
      optional: true,
      label: 'БЕ',
      serializer: yup.array(entityLikeSchema),
    },
    filials: {
      optional: true,
      label: 'Филиалы',
      serializer: yup.array(entityLikeSchema),
    },
    deals: {
      optional: true,
      label: 'Сделки',
      serializer: yup.array(entityLikeSchema),
    },
    costCenters: {
      optional: true,
      label: 'Центры затрат',
      serializer: yup.array(entityLikeSchema),
    },
    paymentScenarion: {
      label: 'Сценарий',
      serializer: entityLikeSchema,
    },
  },
);
